import { useEffect, useRef, useState } from "react";
import { Play, Sparkle } from "@phosphor-icons/react";
import { api, act, useFetch, fmtDate } from "@/lib/api";
import { PageHeader, Panel, Badge, Md, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const AGENTS = [
  ["AI sales & marketing manager", "Plans, delegates, enforces one owner per conversation", "P0"],
  ["Research & data steward", "Verifies, normalises, holds unknown-permission data", "P0"],
  ["Content producer", "Drafts sequences from approved claims only", "P0"],
  ["Outreach coordinator", "Enrols eligible contacts, executes due steps", "P0"],
  ["Conversation agent", "Classifies replies, answers from FAQs, stops or hands off", "P0"],
  ["Scheduling agent", "Proposes rep slots, books, confirms", "P0"],
  ["Performance analyst", "Attributes spend & outcomes, writes briefings", "P0"],
  ["Voice qualifier", "Outbound AI calls with disclosure", "P1"],
  ["Social engagement agent", "Permitted engagement → lead signals", "P1"],
];
const LV = { info: "text-zinc-300", warn: "text-[#FFB020]", error: "text-[#FF3B30]" };

export default function Manager() {
  const { data: runs, reload } = useFetch("/manager/runs");
  const [run, setRun] = useState(null);
  const [instr, setInstr] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: br, reload: reloadBr } = useFetch("/briefings");
  const timer = useRef();

  const poll = (id) => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      const r = await api.get(`/manager/runs/${id}`).catch(() => null);
      if (!r) return;
      setRun(r.data);
      if (r.data.status !== "running") { clearInterval(timer.current); reload(); reloadBr(); }
    }, 1500);
  };
  useEffect(() => () => clearInterval(timer.current), []);
  useEffect(() => { if (runs?.[0] && !run) { setRun(runs[0]); if (runs[0].status === "running") poll(runs[0].id); } }, [runs]); // eslint-disable-line

  const start = async () => {
    const r = await act(() => api.post("/manager/run"), "AI Manager run started");
    if (r) { setRun({ ...r, feed: [] }); poll(r.id); }
  };
  const instruct = async () => {
    setBusy(true);
    const r = await act(() => api.post("/charter/instruct", { text: instr }), "Proposed change sent to Exceptions for your confirmation");
    if (r) setInstr("");
    setBusy(false);
  };

  return (
    <div data-testid="manager-page">
      <PageHeader eyebrow="AIO02 · AI sales & marketing manager" title="Run console" desc="One run = verify data, draft/launch within charter, enrol eligible contacts, execute due steps, handle replies, brief you.">
        <Button data-testid="run-manager-btn" onClick={start} disabled={run?.status === "running"} className="bg-[#007AFF] hover:bg-[#3395FF] gap-2"><Play weight="fill" /> {run?.status === "running" ? "Running…" : "Run AI team now"}</Button>
      </PageHeader>
      <div className="grid lg:grid-cols-3 gap-3">
        <Panel title="Agent activity feed" className="lg:col-span-2" action={run && <span className="flex items-center gap-2"><Badge v={run.status} /><span className="font-mono text-[11px] text-zinc-500">{fmtDate(run.started_at)}</span></span>}>
          <div className="bg-black border border-border rounded-sm p-4 font-mono text-[12px] h-[420px] overflow-y-auto grid-bg" data-testid="agent-activity-feed">
            {!run ? <div className="text-zinc-500">No runs yet. Press “Run AI team now”.</div> : (
              <>
                {(run.feed || []).map((f, i) => (
                  <div key={i} className={`rise mb-1.5 ${LV[f.level]}`} style={{ animationDelay: `${i * 30}ms` }}>
                    <span className="text-zinc-600">{f.at.slice(11, 19)}</span> <span className="text-[#3395FF]">[{f.agent}]</span> {f.message}
                  </div>
                ))}
                {run.status === "running" && <div className="text-zinc-500 caret">working</div>}
              </>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {(runs || []).slice(0, 8).map((r) => (
              <button key={r.id} data-testid={`run-history-${r.id}`} onClick={() => setRun(r)} className="text-[11px] font-mono border border-border px-2 py-1 rounded-sm hover:border-zinc-500 transition-colors">{fmtDate(r.started_at)} · {r.status}</button>
            ))}
          </div>
        </Panel>
        <div className="space-y-3">
          <Panel title="Tell the manager">
            <Textarea data-testid="manager-instruction-input" value={instr} onChange={(e) => setInstr(e.target.value)} rows={4} placeholder="e.g. Focus on Victorian firms this month and lower daily sends to 25" className="bg-black" />
            <Button data-testid="manager-instruction-submit" disabled={!instr || busy} onClick={instruct} variant="secondary" className="mt-3 w-full gap-2"><Sparkle /> {busy ? "Interpreting…" : "Propose charter change"}</Button>
            <p className="text-[11px] text-zinc-500 mt-2">Material changes to delegated authority always need your confirmation in Exceptions.</p>
          </Panel>
          <Panel title="Specialist agents">
            <ul className="space-y-2.5">{AGENTS.map(([n, d, p]) => (
              <li key={n} className="flex gap-3 items-start"><Badge v={p === "P0" ? "active" : "planned"} tone={p === "P0" ? "green" : "gray"} /><div><div className="text-sm">{n}</div><div className="text-[11px] text-zinc-500">{d}</div></div></li>
            ))}</ul>
          </Panel>
        </div>
        <Panel title="Management briefing" className="lg:col-span-3" action={<Button size="sm" variant="secondary" data-testid="generate-briefing-btn" onClick={async () => { await act(() => api.post("/briefings"), "Briefing generated"); reloadBr(); }}>Generate now</Button>}>
          {br?.[0] ? <Md text={br[0].text} /> : <Empty>No briefing yet.</Empty>}
        </Panel>
      </div>
    </div>
  );
}
